import { Component, Input, NgZone, OnDestroy } from '@angular/core';
import { MeteorObservable } from 'meteor-rxjs';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Subject,
  Subscription,
  takeUntil,
} from 'rxjs';
import { inviteesCancelledCancelReason } from 'server/helpers/defaultCancelReasons';
import { Customer } from 'server/shared/models/customer';
import { InpoolLanguage } from 'server/shared/models/InpoolLanguage';
import { Job } from 'server/shared/models/job';
import { InviteeStatusEnum } from 'server/shared/models/jobInviteeStatusEnum';
import { JobStatusEnum } from 'server/shared/models/jobStatusEnum';
import { SexEnum } from 'server/shared/models/sex.enum';
import { UserService } from 'src/app/services/user.service';
import {
  isNonDefaultCustomCriterion,
  NonDefaultCustomCriteriaRow,
  toNonDefaultCustomCriteriaRow,
} from 'src/helpers/customCriteriaRowHelpers';
import { filterIsNotEmpty } from 'src/helpers/filterIsNotEmpty';
import { Column } from 'src/helpers/job/jobColumns';
import { CustomerService } from '../../../services/customer.service';
import { JobRequestService } from '../../../services/job-request.service';
import { TranslationService } from '../../../services/translation.service';

@Component({
  selector: 'app-job-request-item',
  templateUrl: './job-request-item.html',
  styleUrls: [
    './job-request-item.scss',
    '../../../../stylesheets/components/atoms/jobs-list-view.scss',
  ],
})
export class JobRequestItemComponent implements OnDestroy {
  destroyed$ = new Subject<void>();
  lang: InpoolLanguage = this.translationService.getLanguage();
  jobRequest$ = new BehaviorSubject<Job | undefined>(undefined);
  inviteesCancelledCancelReason = inviteesCancelledCancelReason;
  @Input() set jobRequest(job: Job | undefined) {
    if (job) {
      job.invitees =
        job.invitees?.filter((invitee) => {
          return (
            invitee.status === InviteeStatusEnum.BOOKED ||
            invitee.status === InviteeStatusEnum.CANCELED
          );
        }) || [];

      if (job.status === JobStatusEnum.ARCHIVED) {
        if (
          job.invitees.some(
            (invitee) => invitee.status === InviteeStatusEnum.BOOKED,
          )
        ) {
          job.status = JobStatusEnum.CLOSED;
        } else if (
          job.jobRequestRejected ||
          job.requestRetractedAt ||
          job.cancelReason
        ) {
          job.status = JobStatusEnum.CANCELED;
        }
      }
    }
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }

    this.userSubscription = MeteorObservable.subscribe(
      'usersOfJob',
      job?._id,
      true,
    ).subscribe();
    this.jobRequest$.next(job);
  }
  get jobRequest(): Job | undefined {
    return this.jobRequest$.getValue();
  }

  @Input() readOnly?: boolean;
  @Input() canApprove?: boolean;

  // in timeline the date & organisation is redundant, so we use this option
  @Input() showJobDate: boolean = false;
  @Input() showDepartment: boolean = false;

  // for list view
  @Input() compact: boolean = false;
  @Input() columns: Column[] = [];

  sexEnum = SexEnum;
  loggedInCustomer?: Customer;

  dispoCustomer$ = this.customerService.readyDispoCustomer$;
  dispoPermissions$ = this.userService.dispoPermissions$;

  canApproveJobRequests$ = combineLatest([
    this.dispoCustomer$,
    this.dispoPermissions$,
  ]).pipe(
    map(
      ([dispoCustomer, dispoPermissions]) =>
        dispoPermissions.dispoAccess?.featurePermissions?.canApproveJobRequests,
    ),
    distinctUntilChanged(),
  );

  nonDefaultCustomCriteriaRows$ = combineLatest([
    this.dispoCustomer$,
    this.jobRequest$.pipe(filter(filterIsNotEmpty)),
  ]).pipe(
    map(([loggedInCustomer, jobRequest]) => {
      const customerId = loggedInCustomer?._id;
      if (!customerId) {
        return [] as NonDefaultCustomCriteriaRow[];
      }
      const criteriaMap =
        jobRequest.customerInternalInformation?.[customerId]?.customCriteria;
      if (!criteriaMap) {
        return [];
      }
      return Object.values(criteriaMap)
        .filter(isNonDefaultCustomCriterion)
        .map(toNonDefaultCustomCriteriaRow);
    }),
  );

  userSubscription?: Subscription;

  constructor(
    private ngZone: NgZone,
    private translationService: TranslationService,
    private jobRequestService: JobRequestService,
    private customerService: CustomerService,
    private userService: UserService,
  ) {
    this.dispoCustomer$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((customer) => {
        this.loggedInCustomer = customer;
      });
  }

  retractRequest(): void {
    if (!this.jobRequest) {
      return;
    }
    this.jobRequestService.retractJobRequest(this.jobRequest._id).then();
  }

  requestRetractionOnRequest(): void {
    if (!this.jobRequest) {
      return;
    }
    this.jobRequestService
      .requestRetractionOnRequest(this.jobRequest._id)
      .then();
  }

  approveRequest(): void {
    if (!this.jobRequest) {
      return;
    }
    this.jobRequestService.approveJobRequest(this.jobRequest._id).then();
  }

  rejectRequest(): void {
    if (!this.jobRequest) {
      return;
    }
    this.jobRequestService.rejectJobRequest(this.jobRequest._id).then();
  }

  approveRetractionOnRequest(): void {
    if (!this.jobRequest) {
      return;
    }
    this.jobRequestService
      .approveRetractionOnRequest(this.jobRequest._id)
      .then();
  }

  rejectRetractionOnRequest(): void {
    if (!this.jobRequest) {
      return;
    }
    this.jobRequestService
      .rejectRetractionOnRequest(this.jobRequest._id)
      .then();
  }

  ngOnDestroy(): void {
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  readonly JobStatusEnum = JobStatusEnum;
  readonly InviteeStatusEnum = InviteeStatusEnum;

  trackByColumnKey = (index: number, column: Column) => column.key;

  trackByCustomCriteriaLabel(
    _index: number,
    row: NonDefaultCustomCriteriaRow,
  ): string {
    return row.label;
  }
}
